<template>
	<div
		v-if="modal"
		v-show="show"
		class="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-8 bg-ihh-midnight-gray/50 backdrop-blur-sm"
		role="dialog"
		aria-modal="true"
		aria-label="Search products and birth kits"
		@click="$emit('hide')"
	>
		<div
			@click.stop
			class="flex flex-col w-full max-w-7xl h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden"
		>
			<div class="shrink-0 px-6 pt-5 pb-6 sm:px-8 sm:pt-6 border-b border-[#E7E2F0]">
				<div class="flex w-full justify-end">
					<button type="button" @click="$emit('hide')" class="group py-2" aria-label="Close search">
						<div class="m-auto items-center flex w-full justify-center">
							<div class="rotate-45 absolute rounded bg-ihh-slate-gray group-hover:bg-ihh-dark-salmon py-px px-2.5"></div>
							<div class="-rotate-45 rounded bg-ihh-slate-gray group-hover:bg-ihh-dark-salmon py-px px-2.5"></div>
						</div>
					</button>
				</div>

				<div class="text-ihh-midnight-gray font-semibold flex w-full justify-center text-center text-2xl">Search products and birth kits</div>

				<div class="flex w-full max-w-3xl mx-auto mt-6 group rounded-lg text-sm bg-gradient-to-b from-ihh-pink to-ihh-purple font-thin text-black">
					<input
						type="text"
						ref="search"
						v-model="search.value"
						class="px-3 mr-1 z-10 bg-transparent rounded-lg focus:border focus:border-ihh-dark-salmon focus:outline-0 focus:shadow shadow-ihh-dark-salmon w-full text-sm font-light text-black placeholder-ihh-gray relative"
						placeholder="Search the store"
					/>
					<button type="button" class="duration-100 transition bg-transparent hover:bg-ihh-salmon/40 rounded-r-lg right-0 p-3" aria-label="Search">
						<img src="/images/ob-search0129.svg" class="w-5 h-5 duration-300 transition ease-in-out" alt="" />
					</button>
				</div>
			</div>

			<div class="flex-1 min-h-0 overflow-y-auto px-6 py-6 sm:px-8">
				<div v-if="search?.value" class="w-full flex items-center justify-center">
					<Spinner v-if="search.loading" :small="true" class="w-8 h-8" />
					<div v-else class="w-full">
						<div v-if="!search.categories?.length && !search.results?.length" class="text-center text-ihh-midnight-gray text-lg font-light">
							No results found for "{{ search.value }}"
						</div>
						<div v-if="search.categories?.length" class="mb-8 pb-8 border-b">
							<div class="uppercase tracking-widest text-xl text-black mb-4">Categories</div>
							<div class="w-full grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
								<router-link
									class="relative group cursor-pointer flex flex-col w-full aspect-square justify-center bg-magnolia-50/60 shadow rounded-xl items-center p-2 lg:p-6 overflow-hidden product-card"
									v-for="link in search.categories"
									:key="link.url.path"
									:to="`/categories${link.url?.path}`"
								>
									<div class="relative group cursor-pointer flex w-full justify-center bg-ihh-light-purple group-hover:border-2 border-ihh-midnight-gray rounded-xl items-center p-1 overflow-hidden">
										<div class="w-full bg-center bg-contain aspect-square mix-blend-darken rounded-lg" :style="`background-image: url(${link.imageUrl})`" />
									</div>
									<div class="flex w-full justify-center mt-2 text-sm flex-col md:flex-row">
										<div class="text-center font-semibold tracking-wide text-ihh-midnight-gray break-words">{{ link.name }}</div>
									</div>
								</router-link>
							</div>
						</div>
						<div v-if="search.results?.length">
							<div class="uppercase tracking-widest text-xl text-black mb-4">Products</div>
							<div class="w-full grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
								<Product
									v-for="(p, index) in search.results"
									:queryID="queryID"
									:searchResult="true"
									:position="index"
									:key="p.id"
									:product="p"
									class="w-full"
									:navigate="navigate"
								/>
							</div>
						</div>
					</div>
				</div>

				<div v-if="search.value === null || search.value === ''">
					<ImageScroller label="Recommended" :hideButtons="false">
						<Product
							v-for="p in popularProducts"
							:product="p"
							:key="p.id"
							:hideReviews="false"
							:small="false"
							:isUpsellItem="false"
							class="snap-start w-40 shrink-0"
						/>
					</ImageScroller>
				</div>
			</div>
		</div>
	</div>

	<div
		v-else
		class="h-screen fixed overflow-y-scroll top-0 right-0 z-40 select-none w-full transition-colors transform-gpu ease-in-out duration-500 bg-local backdrop-blur"
		:class="show ? 'bg-magnolia-100' : '-translate-y-full pointer-events-none'"
		@click="$emit('hide')"
	>
		<div @click.stop class="flex w-full justify-center py-8 xs:py-12 px-8 bg-ihh-white">
			<div
				class="flex flex-col w-full max-w-2xl h-full justify-center items-center space-y-6 top-0 transform transition-transform duration-300 ease-in-out"
				:class="show ? '' : '-translate-y-full'"
			>
				<div class="flex w-full justify-end">
					<button @click="$emit('hide')" class="group py-2">
						<div class="m-auto items-center flex w-full justify-center">
							<div class="rotate-45 absolute rounded bg-ihh-slate-gray group-hover:bg-ihh-dark-salmon py-px px-2.5"></div>
							<div class="-rotate-45 rounded bg-ihh-slate-gray group-hover:bg-ihh-dark-salmon py-px px-2.5"></div>
						</div>
					</button>
				</div>

				<!-- Dropdown Search Bar -->
				<div class="text-ihh-midnight-gray font-semibold flex w-full justify-center text-center text-2xl">Search products and birth kits</div>

				<div class="flex w-full group rounded-lg text-sm bg-gradient-to-b from-ihh-pink to-ihh-purple font-thin text-black rounded-lg">
					<input
						type="text"
						id="name"
						ref="search"
						v-model="search.value"
						required
						class="px-3 mr-1 z-10 bg-transparent rounded-lg focus:border focus:border-ihh-dark-salmon focus:outline-0 focus:shadow shadow-ihh-dark-salmon w-full text-sm font-light text-black placeholder-ihh-gray relative"
						placeholder="Search the store"
					/>
					<button class="duration-100 transition bg-transparent hover:bg-ihh-salmon/40 rounded-r-lg right-0 p-3">
						<img src="/images/ob-search0129.svg" class="w-5 h-5 duration-300 transition ease-in-out" alt="Search Icon" />
					</button>
				</div>
			</div>
		</div>

		<div v-if="search?.value" class="w-full p-4 md:p-8 flex items-center justify-center">
			<Spinner v-if="search.loading" :small="true" class="w-8 h-8" />
			<div v-else class="max-w-5xl">
				<div v-if="!search.categories?.length && !search.results?.length" class="text-center text-ihh-midnight-gray text-lg font-light">
					No results found for "{{ search.value }}"
				</div>
				<!-- <Product v-for="p in search.results.filter(r => r.collection == 'categories')" :key="p.id" :product="p" class="w-full" /> -->
				<div v-if="search.categories?.length" class="mb-8 pb-8 md:mb-16 md:pb-16 border-b">
					<div class="uppercase tracking-widest text-xl text-black mb-4 text-center md:text-left">Categories</div>
					<div class="w-full grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
						<router-link
							@click="hoverMenu = null"
							class="relative group cursor-pointer flex flex-col w-full aspect-square justify-center bg-magnolia-50/60 shadow rounded-xl items-center p-2 lg:p-6 overflow-hidden product-card"
							v-for="link in search.categories"
							:key="link.url.path"
							:to="`/categories${link.url?.path}`"
						>
							<div
								class="relative group cursor-pointer flex w-full justify-center bg-ihh-light-purple group-hover:border-2 border-ihh-midnight-gray rounded-xl items-center p-1 overflow-hidden"
							>
								<div class="w-full bg-center bg-contain aspect-square mix-blend-darken rounded-lg" :style="`background-image: url(${link.imageUrl})`" />
							</div>
							<div class="flex w-full justify-center mt-2 text-sm flex-col md:flex-row">
								<div class="text-center font-semibold tracking-wide text-ihh-midnight-gray break-words">{{ link.name }}</div>
							</div>
						</router-link>
					</div>
				</div>
				<div v-if="search.results?.length">
					<div class="uppercase tracking-widest text-xl text-black mb-4 text-center md:text-left">Products</div>
					<div class="w-full grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
						<Product
							v-for="(p, index) in search.results"
							:queryID="queryID"
							:searchResult="true"
							:position="index"
							:key="p.id"
							:product="p"
							class="w-full"
							:navigate="navigate"
						/>
					</div>
				</div>
			</div>
		</div>
		<!-- Popular Products -->
		<div v-if="search.value === null || search.value === ''" class="px-8 mt-10 md:mt-14 lg:mt-16">
			<!-- Desktop/XL View -->
			<div class="hidden md:block">
				<ImageScroller label="Popular Products" :hideButtons="false">
					<Product
						v-for="p in popularProducts"
						:product="p"
						:key="p.id"
						:hideReviews="false"
						:small="false"
						:isUpsellItem="false"
						class="snap-start w-40 shrink-0"
					/>
				</ImageScroller>
			</div>

			<!-- Mobile/Tablet/Large View (up to lg) -->
			<div class="md:hidden">
				<div class="text-ihh-midnight-gray text-2xl font-bold tracking-wide whitespace-nowrap text-center">Popular Products</div>
				<div class="w-full grid grid-cols-2 gap-4 sm:grid-cols-3 mt-12">
					<Product v-for="p in popularProducts" :key="p.id" :product="p" :hideReviews="true" :publicList="true" :small="true" class="w-40" />
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import Mango from '../../helpers/mango'
import { loadOrFallback } from '../../helpers/pageLoad'
import Product from '../products/product.vue'
import ImageScroller from '../pages/ihh-items/image-scroller.vue'

export default {
	components: { Product, ImageScroller },
	inject: ['store'],
	props: {
		show: { type: Boolean },
		categoriesEnabled: { default: true },
		searchValue: { default: null },
		modal: { type: Boolean, default: false },
		navigate: { type: Boolean, default: true },
	},
	data() {
		return {
			search: {
				activeIndex: 0,
				value: null,
				results: [],
				categories: [],
				loading: false,
			},
			queryID: null,
			popularProducts: [],
		}
	},
	async created() {
		let fields = ['id', 'name', 'customUrl', 'images', 'alt', 'rating', 'bcId', 'sku', 'popular', 'popularOrder']
		let response = await loadOrFallback(() => Mango.products({ search: { popular: true }, limit: 20 }), [])
		if (!Array.isArray(response)) response = []
		response.sort((a, b) => a.popularOrder - b.popularOrder)
		this.popularProducts = response
	},
	watch: {
		// Add a SLIGHT delay when opening for the first time
		hoverMenu(n) {
			if (!n) return (this.confirmedHoverMenu = n)
			if (this.confirmedHoverMenu) return (this.confirmedHoverMenu = n)
			setTimeout(() => {
				if (n == this.hoverMenu) return (this.confirmedHoverMenu = n)
			}, 100)
		},
		show() {
			if (this.show) {
				this.freeze()
				this.$nextTick(() => {
					this.$refs.search.focus()
					// this.$refs.search.select()
				})
			} else {
				this.thaw()
				this.search.results = []
				this.search.value = null
				this.search.categories = []
			}
		},
		'search.value': {
			async handler(n) {
				this.debounceSearch()
			},
		},
		searchValue(newVal) {
			if (newVal) {
				this.search.value = newVal
			}
		},
	},
	methods: {
		freeze() {
			this.$nextTick(() => {
				// this.$refs.search?.focus()
				this.fadeIn = true
				document.body.style.overflow = 'hidden'
				document.getElementById('app').setAttribute('aria-hidden', 'true')
				this.$refs?.modal?.setAttribute('aria-modal', 'true')
				// this.attachListeners()
			})
		},
		thaw() {
			document.body.style.overflow = ''
			document.getElementById('app').setAttribute('aria-hidden', 'false')
			this.$refs?.modal?.removeAttribute('aria-modal')
		},
		async searchMango() {
			// this.search.results = (await Mango.products.search(this.search.value, {limit: 20}, 'isVisible:true AND availability:available'))?.hits
			this.search.loading = true
			if (this.categoriesEnabled) {
				Mango.search(this.search.value, { limit: 10 }, 'isVisible:true AND collection:categories')
					.then((r) => {
						this.search.categories = this.uniqueCategories(r.hits)
						this.queryID = r.queryID
					})
					.finally(() => (this.search.loading = false))
			}

			// Mango.search(this.search.value, { limit: 25 }, `isVisible:true AND availability:available${this.store.editingKit?.id ? ` AND NOT collection:birthKits` : ''}`)
			Mango.search(
				this.search.value,
				{ limit: 25 },
				`isVisible:true AND NOT categories:66d8b83f6e195ee1d5e056a3${this.store.editingKit?.id ? ` AND NOT collection:birthKits` : ''}`,
			)
				.then((r) => {
					console.log('results', r)
					this.search.results = r.hits.filter((r) => r.collection == 'products' || r.collection == 'birthKits')
					this.queryID = r.queryID
					console.log('filtered results', this.search.results)
				})
				.finally(() => (this.search.loading = false))
		},
		uniqueCategories(results) {
			if (!Array.isArray(results)) {
				console.error('Expected an array in uniqueCategories but got:', results)
				return []
			}
			let uniqueResults = []
			results.forEach((result) => {
				if (!uniqueResults.some((r) => r._id == result._id)) uniqueResults.push(result)
			})
			return uniqueResults.slice(0, 5)
		},
	},
	computed: {
		debounceSearch() {
			// return (...args) => {
			//     clearTimeout(this.searchTimeout);
			//     this.searchTimeout = setTimeout(() => { this.searchMango.apply(this, args) }, 500);
			// };
			return () => {
				clearTimeout(this.searchTimeout)
				this.searchTimeout = setTimeout(() => {
					this.searchMango()
				}, 500)
			}
		},
	},
}
</script>

<style></style>
